import React from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import {
  LayoutDashboard, ListChecks, PhoneCall, FileText, MapPin, BookOpen,
  ClipboardList, BarChart3, Users, ShieldCheck, LogOut, User, Phone,
} from "lucide-react";

const traineeNav = [
  { to: "/dashboard", icon: LayoutDashboard, key: "dashboard" },
  { to: "/mcq", icon: ListChecks, key: "mcq" },
  { to: "/live-call", icon: PhoneCall, key: "liveCall" },
  { to: "/ncrp-form", icon: FileText, key: "ncrpForm" },
  { to: "/case-studies", icon: MapPin, key: "caseStudies" },
  { to: "/reference", icon: BookOpen, key: "reference" },
  { to: "/sop", icon: ClipboardList, key: "sop" },
  { to: "/analytics", icon: BarChart3, key: "analytics" },
  { to: "/profile", icon: User, key: "profile" },
];

const adminNav = [
  { to: "/admin", icon: ShieldCheck, key: "adminDashboard" },
  { to: "/admin/users", icon: Users, key: "users" },
  { to: "/admin/content", icon: FileText, key: "content" },
  { to: "/admin/analytics", icon: BarChart3, key: "analytics" },
  { to: "/case-studies", icon: MapPin, key: "caseStudies" },
  { to: "/reference", icon: BookOpen, key: "reference" },
  { to: "/profile", icon: User, key: "profile" },
];

const OFFICIAL_LANGUAGES = [
  { code: "en", name: "English" },
  { code: "hi", name: "हिन्दी (Hindi)" },
  { code: "as", name: "অসমীয়া (Assamese)" },
  { code: "bn", name: "বাংলা (Bengali)" },
  { code: "brx", name: "बड़ो (Bodo)" },
  { code: "doi", name: "डोगरी (Dogri)" },
  { code: "gu", name: "ગુજરાતી (Gujarati)" },
  { code: "kn", name: "ಕನ್ನಡ (Kannada)" },
  { code: "ks", name: "कॉशुर (Kashmiri)" },
  { code: "kok", name: "कोंकणी (Konkani)" },
  { code: "mai", name: "मैथिली (Maithili)" },
  { code: "ml", name: "മലയാളം (Malayalam)" },
  { code: "mni", name: "মৈতৈলোন্ (Manipuri)" },
  { code: "mr", name: "मराठी (Marathi)" },
  { code: "ne", name: "नेपाली (Nepali)" },
  { code: "or", name: "ଓଡ଼ିଆ (Odia)" },
  { code: "pa", name: "ਪੰਜਾਬੀ (Punjabi)" },
  { code: "sa", name: "संस्कृतम् (Sanskrit)" },
  { code: "sat", name: "ᱥᱟᱱᱛᱟᱲᱤ (Santali)" },
  { code: "sd", name: "سنڌي (Sindhi)" },
  { code: "ta", name: "தமிழ் (Tamil)" },
  { code: "te", name: "తెలుగు (Telugu)" },
  { code: "ur", name: "اردو (Urdu)" }
];

function LangControl() {
  const { lang, setLanguage } = useLang();
  return (
    <div className="flex items-center gap-3 text-xs text-slate-300">
      <select
        value={lang}
        onChange={(e) => setLanguage(e.target.value)}
        className="bg-transparent text-white font-semibold cursor-pointer outline-none hover:text-slate-200 transition-colors"
      >
        {OFFICIAL_LANGUAGES.map((l) => (
          <option key={l.code} value={l.code} className="text-slate-900">
            {l.name}
          </option>
        ))}
      </select>
      <span className="hidden md:inline text-slate-500 ml-1">A- A A+</span>
    </div>
  );
}

export function NcrpBanner() {
  const { t } = useLang();
  return (
    <div className="w-full" data-testid="ncrp-banner">
      {/* tricolor top edge */}
      <div className="flex h-1">
        <div className="flex-1 bg-[#FF9933]" />
        <div className="flex-1 bg-white" />
        <div className="flex-1 bg-[#138808]" />
      </div>

      {/* Government of India strip */}
      <div className="bg-[#161616] text-slate-300 px-4 md:px-8 py-1.5 flex items-center justify-between text-xs" data-testid="gov-strip">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold border border-slate-600 rounded px-1 text-slate-300">IN</span>
          <span className="font-semibold text-slate-100">{t("govOfIndia")}</span>
          <span className="text-slate-600 hidden sm:inline">|</span>
          <span className="hidden sm:inline">{t("mha")}</span>
        </div>
        <LangControl />
      </div>

      {/* Main NCRP banner */}
      <div className="bg-[#1b1f27] px-4 md:px-8 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <img src="/emblem-gold.png" alt="National Emblem of India" className="h-14 w-auto object-contain shrink-0" />
          <div className="min-w-0">
            <div className="font-head font-bold text-slate-100 text-base md:text-lg leading-tight truncate">{t("portalHi")}</div>
            <div className="font-head font-bold text-[#8fb4dd] text-sm md:text-base uppercase tracking-wide leading-tight truncate">{t("portalEn")}</div>
            <div className="text-[11px] text-slate-400 hidden md:block truncate">{t("portalTag")}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-2 rounded-lg bg-[#c1272d] text-white px-4 py-2 shadow-sm" data-testid="helpline-1930">
            <Phone className="h-6 w-6 shrink-0" />
            <div className="leading-none">
              <div className="font-bold text-xl">1930</div>
              <div className="text-[9px] tracking-wide">{t("natlHelpline")}</div>
            </div>
          </div>
          <div className="rounded-lg bg-[#2b3a52] text-white px-3 py-2 text-center leading-tight hidden sm:block" data-testid="i4c-badge">
            <div className="font-bold text-lg">I4C</div>
            <div className="text-[8px] text-slate-300 max-w-[110px]">{t("i4cCentre")}</div>
          </div>
        </div>
      </div>

      {/* Helpline marquee bar */}
      <div className="bg-[#0d1b3e] text-white flex items-stretch overflow-hidden border-y border-[#c1272d]/40">
        <div className="bg-[#c1272d] text-white text-xs font-bold px-3 flex items-center shrink-0 uppercase tracking-wide">
          Helpline 1930
        </div>
        <div className="marquee-wrap flex-1 overflow-hidden relative">
          <div className="marquee-track py-1.5 text-xs text-slate-200">
            <span className="px-8">{t("marquee")}</span>
            <span className="px-8">{t("marquee")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = user?.role === "admin" ? adminNav : traineeNav;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <NcrpBanner />

      <div className="flex flex-1 min-h-0">
        <aside className="w-60 bg-[#0F172A] text-slate-200 flex flex-col shrink-0 sticky top-0 self-start h-screen" data-testid="sidebar">
          <div className="px-5 py-4 border-b border-slate-700/60">
            <div className="font-head font-bold text-white leading-tight text-sm">{t("appName")}</div>
            <div className="text-[11px] text-slate-400">{t("appSub")}</div>
          </div>
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.key}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  }`
                }
              >
                <n.icon className="h-4 w-4 shrink-0" />
                {t(n.key)}
              </NavLink>
            ))}
          </nav>
          <div className="px-3 py-3 border-t border-slate-700/60">
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
            >
              <LogOut className="h-4 w-4" /> {t("logout")}
            </button>
          </div>
        </aside>

        <div className="flex-1 min-w-0 flex flex-col">
          <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-8">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                {user?.badge_number}
              </span>
              <span className="text-slate-300">•</span>
              <span>{user?.designation} · {user?.state}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-[#0F172A] text-white flex items-center justify-center text-xs font-semibold">
                {user?.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="text-sm">
                <div className="font-medium text-slate-800 leading-tight">{user?.name}</div>
                <div className="text-[11px] text-slate-400 capitalize">{user?.role}</div>
              </div>
            </div>
          </header>
          <main className="flex-1 p-8 animate-fade-in" data-testid="main-content">{children}</main>
        </div>
      </div>
    </div>
  );
}
